// src/components/compliance/modals/InProgressTasksModal.jsx

import React from "react";
import {
  buildControlLabel,
  cleanValue,
  formatTimestamp,
  getTaskStatusLabel,
  normalizeText,
  safeNumber,
} from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function getTaskTitle(task = {}) {
  return (
    cleanValue(task.title, "") ||
    cleanValue(task.task_title, "") ||
    `Remediate ${buildControlLabel(task.control_id || task.controlId, task.rule_id || task.ruleId)}`
  );
}

function getTaskOwner(task = {}) {
  return cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
}

function getTaskResource(task = {}) {
  return cleanValue(task.resource_id || task.resourceId, "—");
}

function getUpdatedAt(task = {}) {
  return task.updated_at || task.updatedAt || task.created_at || task.createdAt || null;
}

function isHighPriority(task = {}) {
  return safeNumber(task.priority, 0) >= 90;
}

function isOverdue(task = {}) {
  return Boolean(task.is_overdue) || normalizeText(task.sla_state) === "overdue";
}

function getDueDateLabel(task = {}) {
  const dueDate = task.due_date || task.dueDate || task.deadline_at || task.deadlineAt || "";
  if (!dueDate) return "No due date";
  return cleanValue(dueDate, "No due date");
}

function getSlaLabel(task = {}) {
  const slaState = cleanValue(task.sla_state || task.slaState, "");
  const daysUntilDue = safeNumber(task.days_until_due, null);

  if (isOverdue(task)) {
    if (Number.isFinite(daysUntilDue)) {
      const overdueDays = Math.abs(Math.min(daysUntilDue, 0));
      return `Overdue by ${overdueDays} day${overdueDays === 1 ? "" : "s"}`;
    }

    return "Overdue";
  }

  if (Number.isFinite(daysUntilDue)) {
    if (daysUntilDue === 0) return "Due today";
    if (daysUntilDue === 1) return "Due tomorrow";
    return `Due in ${daysUntilDue} days`;
  }

  return slaState ? slaState.replace(/_/g, " ") : "SLA active";
}

function buildDriftParams(task = {}, framework) {
  return {
    framework: task.framework || framework,
    controlId: task.control_id || task.controlId || undefined,
    ruleId: task.rule_id || task.ruleId || undefined,
    resourceId: task.resource_id || task.resourceId || undefined,
  };
}

export default function InProgressTasksModal({
  open,
  framework,
  inProgressTasks = [],
  totalCount = 0,
  loading = false,
  error = "",
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const rows = Array.isArray(inProgressTasks) ? inProgressTasks : [];
  const shownCount = rows.length;
  const countLabel =
    totalCount > shownCount ? `${shownCount} shown of ${totalCount}` : `${shownCount} shown`;

  const highPriorityCount = rows.filter(isHighPriority).length;
  const overdueCount = rows.filter(isOverdue).length;
  const assignedCount = rows.filter((task) => normalizeText(getTaskOwner(task)) !== "unassigned").length;

  return (
    <ModalShell
      open={open}
      ariaLabel="In progress remediation work"
      eyebrow="OASYS · IN PROGRESS"
      title="In Progress Remediation Work"
      subtitle="Review remediation tasks currently being worked. Use Review / Manage to update status, Fix Plan for source-system steps, or Guidance for read-only context."
      onClose={onClose}
      maxWidth="1120px"
      accentBorder="rgba(251, 191, 36, 0.45)"
    >
      <div style={metricGridStyle}>
        <MetricTile label="In Progress" value={totalCount || shownCount} sub={`${framework} active work`} color="#fbbf24" />
        <MetricTile
          label="High Priority"
          value={highPriorityCount}
          sub="priority escalation"
          color={highPriorityCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Overdue"
          value={overdueCount}
          sub="SLA breached"
          color={overdueCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Assigned"
          value={assignedCount}
          sub="owned work"
          color={assignedCount > 0 ? "#60a5fa" : "#fbbf24"}
        />
      </div>

      {error ? <div className="warn">{error}</div> : null}

      <ModalSection
        title="In Progress Task Activity"
        description="Track ownership, due dates, validation state, and next remediation actions without expanding the main operations page."
        badge={loading ? "loading…" : countLabel}
        badgeTone="warn"
      >
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>
            Loading in-progress remediation work…
          </div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No in-progress remediation tasks are currently loaded for {framework}.
          </div>
        ) : (
          rows.map((task, index) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const statusLabel = getTaskStatusLabel(task.status || task.task_status || "in_progress");
            const validationLabel = cleanValue(task.validation_status || task.validationStatus, "pending").replace(/_/g, " ");
            const ownerLabel = getTaskOwner(task);
            const dueDateLabel = getDueDateLabel(task);
            const slaLabel = getSlaLabel(task);
            const priority = safeNumber(task.priority, "—");
            const updatedAt = getUpdatedAt(task);
            const taskKey = task.id || `${controlId}-${ruleId}-${task.resource_id || task.resourceId || index}`;

            return (
              <TaskQueueCard
                key={taskKey}
                title={getTaskTitle(task)}
                chips={[
                  { label: statusLabel, tone: "warn" },
                  { label: `P${priority}`, tone: isHighPriority(task) ? "danger" : "default" },
                  { label: ownerLabel, tone: ownerLabel === "Unassigned" ? "warn" : "default" },
                  isOverdue(task) ? { label: "Overdue", tone: "danger" } : null,
                ]}
                dueLabel={updatedAt ? formatTimestamp(updatedAt) : dueDateLabel}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: getTaskResource(task), wrap: true },
                ]}
                context={`Status ${statusLabel} · Validation ${validationLabel} · Due ${dueDateLabel} · SLA ${slaLabel}${task.failure_reason || task.reason_summary || task.remediation_hint ? ` · ${cleanValue(task.failure_reason || task.reason_summary || task.remediation_hint, "Review the task for remediation details.")}` : ""}`}
                contextLabel="Work state"
                actions={[
                  { key: "review", label: "Review / Manage", onClick: () => onReviewTask(task, { returnTo: "in_progress_tasks" }) },
                  { key: "fix", label: "Fix Plan", onClick: () => onFixPlan(task, "task", { returnTo: "in_progress_tasks" }) },
                  { key: "guidance", label: "Guidance", onClick: () => onViewGuidance(task, { returnTo: "in_progress_tasks" }) },
                  { key: "drift", label: "Open in Drift", onClick: () => onOpenDrift(buildDriftParams(task, framework)) },
                ]}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
